import { Box, Button, Stack, Typography } from "@mui/material";
import { AiStudioRoute, AiStudioRouteOption } from "hooks/useAiStudioChat";

/**
 * Shown when the intent classifier was not confident enough to pick a
 * specialist on its own. The backend closed the stream right after this
 * frame -- no specialist ran, no tokens were spent -- so the conversation is
 * genuinely waiting on the user here.
 *
 * Clicking an option re-sends the SAME prompt with that route forced; the
 * hook replaces this card with the real answer, so the user never has to
 * retype the question.
 */

interface RouteChoiceCardProps {
  question: string;
  options: AiStudioRouteOption[];
  onChoose: (route: AiStudioRoute) => void;
  disabled?: boolean;
}

const RouteChoiceCard: React.FC<RouteChoiceCardProps> = ({
  question,
  options,
  onChoose,
  disabled,
}) => {
  if (options.length === 0) return null;

  return (
    <Box
      sx={{
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        p: 1.5,
        my: 0.5,
      }}
    >
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        {question}
      </Typography>
      <Stack spacing={1}>
        {options.map((option) => (
          <Button
            key={option.route}
            variant="outlined"
            fullWidth
            disabled={disabled}
            onClick={() => onChoose(option.route)}
            sx={{
              textTransform: "none",
              justifyContent: "flex-start",
              textAlign: "left",
              flexDirection: "column",
              alignItems: "flex-start",
              py: 1,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {option.label}
            </Typography>
            {option.description && (
              <Typography variant="caption" color="text.secondary">
                {option.description}
              </Typography>
            )}
          </Button>
        ))}
      </Stack>
    </Box>
  );
};

export default RouteChoiceCard;
